export type CloudProvider = 'azure' | 'gcp';
export type ProviderType = 'forge' | 'honsso' | 'mcd';

export function getMCDEnabled(params: string): boolean {
  const MCDSSO = new URLSearchParams(params).get('mcd');
  return MCDSSO !== null && MCDSSO.toLowerCase() === 'true';
}

export interface IdentityProviderConfig {
  authority?: string;
  clientId: string;
  tenantId?: string;
  scopes: string[];
  metadata?: Record<string, string>;
  hostedDomain?: string;
  loginHint?: string;
}

export interface EnvironmentConfig {
  CLOUD_PROVIDER: 'azure' | 'gcp';
  MCD_INSTANCE?: boolean;
  ADMIN_MCD_HOSTNAME?: string;
  IDENTITY_PROVIDERS: {
    azure?: {
      forge?: IdentityProviderConfig;
      honsso?: IdentityProviderConfig;
      mcd?: IdentityProviderConfig;
    };
    gcp?: IdentityProviderConfig;
  };
}

export interface AuthConfig {
  cloudProvider: CloudProvider;
  providerType: ProviderType;
  identityProviders: EnvironmentConfig['IDENTITY_PROVIDERS'];
}

/**
 * Builds IDENTITY_PROVIDERS configuration dynamically from flat ENV values
 */
function buildIdentityProvidersConfig(
  cloudProvider: CloudProvider,
  env: any
): EnvironmentConfig['IDENTITY_PROVIDERS'] {
  if (cloudProvider === 'gcp') {
    return {
      gcp: {
        authority: env.FORGE_IAM_AUTHORITY_GCP,
        clientId: env.FORGE_IAM_CLIENT_ID_GCP,
        scopes: env.API_SCOPES,
      }
    };
  }
  
  return {
    azure: {
      forge: {
        authority: env.AUTHORITY,
        clientId: env.AUTHORITY_CLIENT_ID,
        tenantId: env.TENANT_ID,
        scopes: env.API_SCOPES || ['openid', 'offline', 'profile', 'consup.cloud.api.gateway', 'email']
      },
      honsso: {
        authority: env.AUTHORITY_HON_SSO,
        clientId: env.AUTHORITY_CLIENT_ID_HON_SSO,
        tenantId: env.TENANT_ID,
        scopes: env.API_SCOPES_HON_SSO || ['profile', 'openid', 'email', 'https://consup.cloud.api.gateway.com'],
        metadata: {
          authorization_endpoint: env.HON_SSO_AUTHORIZATION_ENDPOINT,
          token_endpoint: env.HON_SSO_TOKEN_ENDPOINT,
          end_session_endpoint: env.HON_SSO_END_SESSION_ENDPOINT,
          userinfo_endpoint: env.HON_SSO_USERINFO_ENDPOINT
        }
      },
      mcd: {
        authority: env.AUTHORITY,
        clientId: env.AUTHORITY_CLIENT_ID_MCD,
        tenantId: env.TENANT_ID,
        scopes: env.API_SCOPES || ['openid', 'offline', 'profile', 'consup.cloud.api.gateway', 'email'],
        loginHint: env.ADMIN_MCD_LOGIN_HINT ?? env.AZURE_MCD_LOGIN_HINT
      }
    }
  };
}

function getEnvironmentConfig(): EnvironmentConfig {
  if (typeof window !== 'undefined' && window.ENV) {
    const cloudProvider = (window.ENV.CLOUD_PROVIDER || 'azure') as 'azure' | 'gcp';
    const identityProviders = buildIdentityProvidersConfig(cloudProvider, window.ENV);
    
    return {
      ...window.ENV,
      CLOUD_PROVIDER: cloudProvider,
      IDENTITY_PROVIDERS: identityProviders
    };
  }
  
  throw new Error('window.ENV is not defined. Check config.js');
}

function determineProviderType(
  cloudProvider: CloudProvider
): ProviderType {
  const env = getEnvironmentConfig();
  
  if (cloudProvider === 'gcp') {
    return 'forge';
  }
  
  const storedType = localStorage.getItem('providerType') as ProviderType;
  if (storedType && ['forge', 'honsso', 'mcd'].includes(storedType)) {
    return storedType;
  }
  
  const sessionType = sessionStorage.getItem('providerType') as ProviderType;
  if (sessionType && ['forge', 'honsso', 'mcd'].includes(sessionType)) {
    return sessionType;
  }
  
  const hostname = window.location.hostname;
  const hstName = window.location.href;
  const isMCDSSO = 
    window.location.pathname.includes('mcd') ||
    getMCDEnabled(window.location.search) ||
    env.MCD_INSTANCE ||
    (env.ADMIN_MCD_HOSTNAME && env.ADMIN_MCD_HOSTNAME !== '' ? hstName.includes(env.ADMIN_MCD_HOSTNAME) : false);
  
  if (isMCDSSO || hostname.includes('mcd') || hostname.includes('manufacturing')) {
    return 'mcd';
  }
  
  if (localStorage.getItem('selectedLoginOption') === 'SSO' ||
      hostname.includes('ping') || 
      hostname.includes('sso') || 
      hostname.includes('qauthn')) {
    return 'honsso';
  }
  
  return 'forge';
}

export function getAuthConfig(): AuthConfig {
  const env = getEnvironmentConfig();

  const cloudProvider = env.CLOUD_PROVIDER;
  const providerType = determineProviderType(cloudProvider);
  
  return { 
    cloudProvider,
    providerType,
    identityProviders: env.IDENTITY_PROVIDERS
  };
}

export function getAuthConfigWithOverrides(overrides: {
  cloudProvider?: CloudProvider;
  providerType?: ProviderType;
}): AuthConfig {
  const env = getEnvironmentConfig();
  
  const cloudProvider = overrides.cloudProvider ?? env.CLOUD_PROVIDER;
  const providerType = overrides.providerType ?? determineProviderType(cloudProvider);
  
  return {
    cloudProvider,
    providerType,
    identityProviders: env.IDENTITY_PROVIDERS
  };
}
